/**
 * ExportPrescreenListModal — download one CSV of every lead on a study's
 * list: contact info and demographics, the questions they were asked on the
 * landing page / the texted study questionnaire / the recruiter or agent
 * prescreen (one column each), and the eligibility determinations made.
 *
 * The checklist comes from GET /studies/:id/prescreen-export/options — the
 * server publishes what actually exists for this study. "De-identified" is
 * enforced server-side (names, phone, email, DOB, the CRM link and every
 * free-text answer are dropped; dates keep only the year; age caps at 90+);
 * here it only greys those options out so the recruiter sees what will go.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, FileDown, Loader2, ShieldCheck } from 'lucide-react';
import api from '../../../services/api';
import Modal from '../forms/Modal';
import { triggerBlobDownload } from '../../lib';
import {
  ExportCatalog, ExportSection, allFieldKeys, effectiveSelection, sectionState, toggleField, toggleSection,
} from './exportSelection';

interface Props {
  studyId: string;
  open: boolean;
  onClose: () => void;
}

const checkCls = 'h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40';

/** With responseType:'blob', axios delivers JSON error bodies as a Blob. */
async function blobErrMsg(e: any, fallback: string): Promise<string> {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.detail ? `${parsed.error} — ${parsed.detail}` : (parsed.error || fallback);
    } catch { return fallback; }
  }
  if (data?.error) return data.detail ? `${data.error} — ${data.detail}` : data.error;
  return e?.message || fallback;
}

function filenameFromDisposition(disposition?: string, fallback = 'prescreen-list.csv'): string {
  const m = /filename="?([^"]+)"?/.exec(disposition || '');
  return m ? m[1] : fallback;
}

export default function ExportPrescreenListModal({ studyId, open, onClose }: Props) {
  const [catalog, setCatalog] = useState<ExportCatalog | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deidentify, setDeidentify] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setError(null);
    api.get(`/api/crm/studies/${studyId}/prescreen-export/options`)
      .then((res) => {
        if (cancelled) return;
        const cat: ExportCatalog = res.data;
        setCatalog(cat);
        setSelected(new Set(allFieldKeys(cat)));
        // Sections nobody has data in start folded so the list stays scannable.
        setCollapsed(new Set(cat.sections.filter((s) => s.count === 0).map((s) => s.key)));
      })
      .catch((e: any) => {
        if (cancelled) return;
        const d = e?.response?.data;
        setLoadError(d?.detail ? `${d.error} — ${d.detail}` : (d?.error || e?.message || 'Could not load the export options'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, studyId]);

  const fields = useMemo(
    () => (catalog ? effectiveSelection(selected, catalog, deidentify) : []),
    [catalog, selected, deidentify],
  );

  const close = useCallback(() => { if (!busy) onClose(); }, [busy, onClose]);

  const download = async () => {
    if (!catalog || !fields.length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(
        `/api/crm/studies/${studyId}/prescreen-export`,
        { fields, deidentify },
        { responseType: 'blob' },
      );
      triggerBlobDownload(
        new Blob([res.data], { type: 'text/csv;charset=utf-8' }),
        filenameFromDisposition(res.headers?.['content-disposition']),
      );
    } catch (e: any) {
      setError(await blobErrMsg(e, 'Could not build the export'));
    } finally {
      setBusy(false);
    }
  };

  const toggleCollapsed = (key: string) => setCollapsed((c) => {
    const next = new Set(c);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const renderSection = (section: ExportSection) => {
    const state = sectionState(section, selected, deidentify);
    const folded = collapsed.has(section.key);
    const selectable = deidentify ? section.fields.filter((f) => !f.identifying) : section.fields;
    const onCount = selectable.filter((f) => selected.has(f.key)).length;
    return (
      <div key={section.key} className="rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 px-3 py-2">
          <input
            type="checkbox"
            aria-label={`Include ${section.label}`}
            className={checkCls}
            checked={state === 'all'}
            disabled={selectable.length === 0}
            ref={(el) => { if (el) el.indeterminate = state === 'some'; }}
            onChange={() => setSelected((s) => toggleSection(s, section, deidentify))}
          />
          <button
            type="button"
            onClick={() => toggleCollapsed(section.key)}
            aria-expanded={!folded}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          >
            {folded
              ? <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
              : <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />}
            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{section.label}</span>
            <span className="ml-auto flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">
              {onCount}/{selectable.length} columns
              {typeof section.count === 'number' && (
                <> · {section.count} lead{section.count === 1 ? '' : 's'}</>
              )}
            </span>
          </button>
        </div>
        {!folded && (
          <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-700">
            {section.description && (
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{section.description}</p>
            )}
            {section.fields.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500">Nothing to export in this section yet.</p>
            ) : (
              <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                {section.fields.map((f) => {
                  const blocked = deidentify && !!f.identifying;
                  return (
                    <label
                      key={f.key}
                      className={`flex min-h-[44px] items-center gap-2 rounded px-1 text-sm sm:min-h-0 sm:py-1 ${
                        blocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'
                      }`}
                      title={blocked ? 'Removed from a de-identified export' : undefined}
                    >
                      <input
                        type="checkbox"
                        className={checkCls}
                        checked={!blocked && selected.has(f.key)}
                        disabled={blocked}
                        onChange={() => setSelected((s) => toggleField(s, f.key))}
                      />
                      <span className="min-w-0 flex-1 truncate" title={f.label}>{f.label}</span>
                      {f.identifying && (
                        <span className="flex-shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          PII
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal isOpen={open} onClose={close} title="Export prescreening list" maxWidth="max-w-2xl">
      {loading && (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
          Reading what has been collected for this study…
        </div>
      )}
      {loadError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}

      {catalog && !loading && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium text-gray-900 dark:text-gray-100">{catalog.lead_count.toLocaleString()}</span>
            {' '}lead{catalog.lead_count === 1 ? '' : 's'} on <span className="font-medium">{catalog.study.title}</span> — one row each.
            Untick anything you do not want in the file.
          </p>

          <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
            deidentify
              ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
              : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40'
          }`}>
            <input
              type="checkbox"
              className={`mt-0.5 ${checkCls}`}
              checked={deidentify}
              onChange={(e) => setDeidentify(e.target.checked)}
            />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                <ShieldCheck className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                De-identified export
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Removes names, phone, email, address, date of birth, the CRM link and any question that asks for one of
                those (marked PII below). Medical answers stay. Dates keep only the year and ages of 90+ are capped. The
                lead ref — a random ID, nothing about the person — stays so rows can be matched back inside the CRM.
              </span>
            </span>
          </label>

          <div className="space-y-2">{catalog.sections.map(renderSection)}</div>
        </div>
      )}

      {error && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="sticky -bottom-4 -mx-4 -mb-4 mt-4 flex flex-col gap-2 border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:-bottom-6 sm:-mx-6 sm:-mb-6 sm:flex-row sm:items-center sm:px-6">
        {catalog && !loading && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {fields.length} column{fields.length === 1 ? '' : 's'} · {deidentify ? 'de-identified' : 'includes identifiers'}
          </span>
        )}
        <div className="flex gap-2 sm:ml-auto">
          <button
            type="button"
            onClick={close}
            disabled={busy}
            className="min-h-[44px] flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0 sm:flex-none"
          >
            Close
          </button>
          <button
            type="button"
            onClick={download}
            disabled={busy || loading || !catalog || fields.length === 0}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:flex-none"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Download CSV
          </button>
        </div>
      </div>
    </Modal>
  );
}
